import React, { useState, useEffect } from 'react';

const CompletionModal = ({ isOpen, onClose, batch, item, onComplete, processingAction }) => {
    const [quantity, setQuantity] = useState(1);
    const [error, setError] = useState('');

    const maxRemainingQuantity = item.quantity - (item.completed_quantity || 0);

    useEffect(() => {
        // Reset quantity when modal opens or item changes
        setQuantity(1);
        setError('');
    }, [isOpen, item]);

    const handleSubmit = () => {
        if (quantity <= 0) {
            setError('Quantity must be greater than zero');
            return;
        }

        if (quantity > maxRemainingQuantity) {
            setError(`Cannot complete more than the remaining quantity (${maxRemainingQuantity})`);
            return;
        }

        onComplete(batch, item, quantity);
    };

    const handleQuantityChange = (e) => {
        // Use the raw input value instead of parsing as integer
        const inputValue = e.target.value;

        // Only set the state if the value is empty or a valid number
        if (inputValue === '' || /^\d+$/.test(inputValue)) {
            const numValue = inputValue === '' ? '' : parseInt(inputValue, 10);
            setQuantity(numValue);

            // Validate the input value and set error message
            if (numValue !== '' && numValue > maxRemainingQuantity) {
                setError(`Cannot complete more than the remaining quantity (${maxRemainingQuantity})`);
            } else if (numValue !== '' && numValue <= 0) {
                setError('Quantity must be greater than zero');
            } else {
                setError('');
            }
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 overflow-y-auto">
            <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
                <div className="fixed inset-0 transition-opacity" aria-hidden="true">
                    <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
                </div>

                <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>

                <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
                    <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                        <div className="sm:flex sm:items-start">
                            <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-blue-100 sm:mx-0 sm:h-10 sm:w-10">
                                <svg className="h-6 w-6 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                </svg>
                            </div>
                            <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left w-full">
                                <h3 className="text-lg leading-6 font-medium text-gray-900">Complete Production Item</h3>
                                <div className="mt-2">
                                    <p className="text-sm text-gray-500">
                                        Update the completion status for {item.product?.name || 'this item'}.
                                    </p>
                                    <div className="mt-4">
                                        <div className="flex flex-col space-y-2">
                                            <div className="grid grid-cols-2 gap-4">
                                                <div>
                                                    <label className="block text-sm font-medium text-gray-700">Total Quantity</label>
                                                    <p className="mt-1 text-sm text-gray-500">{item.quantity}</p>
                                                </div>
                                                <div>
                                                    <label className="block text-sm font-medium text-gray-700">Previously Completed</label>
                                                    <p className="mt-1 text-sm text-gray-500">{item.completed_quantity || 0}</p>
                                                </div>
                                            </div>
                                            <div>
                                                <label htmlFor="quantity" className="block text-sm font-medium text-gray-700">
                                                    Quantity to Complete Now
                                                </label>
                                                <input
                                                    type="number"
                                                    id="quantity"
                                                    min="1"
                                                    max={maxRemainingQuantity}
                                                    value={quantity}
                                                    onChange={handleQuantityChange}
                                                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                                                    disabled={processingAction}
                                                />
                                            </div>
                                            <div className="grid grid-cols-2 gap-4">
                                                <div>
                                                    <label className="block text-sm font-medium text-gray-700">Remaining After</label>
                                                    <p className="mt-1 text-sm text-gray-500">
                                                        {Math.max(0, maxRemainingQuantity - (quantity || 0))}
                                                    </p>
                                                </div>
                                                <div>
                                                    <label className="block text-sm font-medium text-gray-700">Status</label>
                                                    <p className="mt-1 text-sm text-gray-500">
                                                        {(quantity || 0) >= maxRemainingQuantity ? 'Will be completed' : 'In production'}
                                                    </p>
                                                </div>
                                            </div>
                                            {error && (
                                                <div className="text-sm text-red-600 mt-1">{error}</div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                        <button
                            type="button"
                            onClick={handleSubmit}
                            disabled={processingAction || error !== '' || quantity === '' || quantity <= 0}
                            className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50"
                        >
                            {processingAction ? 'Processing...' : 'Complete'}
                        </button>
                        <button
                            type="button"
                            onClick={onClose}
                            disabled={processingAction}
                            className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50"
                        >
                            Cancel
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CompletionModal;
